import { useEffect, useMemo, useState } from 'react';
import { getUserInfo, getUserRolePermissions } from 'src/services/userServicer';
import { isSessionFailure, redirectToLogin } from 'src/utils/sessionExpiry';

export interface PagePermission {
  show: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
  raw?: any;
}

export const normalizePageName = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toBool = (value: unknown) => Boolean(value);

export const usePagePermission = (pageName: string) => {
  const pageKey = useMemo(() => normalizePageName(pageName), [pageName]);

  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState<PagePermission | null>(null);
  const [notAllowed, setNotAllowed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      // Stays true when we navigate away to /login, so consumers keep their loader up
      // instead of flashing an empty "no permission" page on the way out.
      let leaving = false;
      try {
        const user = await getUserInfo();
        const userId = user?.id;

        if (!userId) {
          if (!isMounted) return;
          setPermission(null);
          setNotAllowed(true);
          return;
        }

        const res = await getUserRolePermissions(userId);

        // A dead session (401) or a rejected CSRF token is not a permission answer —
        // treating it as one showed "You are not allowed to view this page" on every
        // screen while the shell stayed signed in. Bounce to login instead.
        if (isSessionFailure(res)) {
          leaving = true;
          await redirectToLogin();
          return;
        }

        const perms: any[] = res?.success ? res?.data?.permissions || [] : [];
        const raw = perms.find((p) => normalizePageName(p?.page) === pageKey) || null;

        const show = toBool(raw?.show);
        const create = toBool(raw?.create ?? raw?.upload ?? false);
        const edit = toBool(raw?.edit);
        const del = toBool(raw?.delete);

        if (!isMounted) return;

        setPermission({ show, create, edit, delete: del, raw });
        setNotAllowed(!show);
      } catch {
        if (!isMounted) return;
        setPermission(null);
        setNotAllowed(true);
      } finally {
        if (!isMounted || leaving) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [pageKey]);

  return { loading, permission, notAllowed };
};

export default usePagePermission;

